import React, { useCallback, useState, useMemo } from "react";

import PropTypes from "prop-types";

import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Modal from "@cayuse/shared-react.components.modal";
import { isFunction, isNonEmptyArray, isNonEmptyMap, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { useDictionary } from "hooks";
import { alphaSortCollectionByKey, buildCompositeKey } from "utils";

import DownloadingModalContent from "./DownloadingModalContent";
import FormModalContent from "./FormModalContent";
import styles from "./styles.css";

export const MODAL_ID = "downloadToCSVModal";

const handleColumnsListFocus = nextOption => {
	setTimeout(() => {
		const selectedElement = document.getElementById(`checkbox-${nextOption.compositeKey}`);

		if (selectedElement) {
			const allVisibleCheckboxes = document.querySelectorAll("#columns-list-wrapper input[type='checkbox']");

			if (allVisibleCheckboxes) {
				allVisibleCheckboxes.forEach(checkbox => {
					checkbox.tabIndex = -1;
				});
			}

			selectedElement.tabIndex = 0;
			selectedElement.focus();
		}
	}, 100);
}

const DownloadCSVModal = ({
	onCancel,
	onClick,
	onSave,
	isOpen,
	options, /* these should be shaped like columnHeaders from the tenant */
	selections: initialSelections,
	isDownloading,
	showButton = true,
	popupTitle,
	popupDescription,
	executeButtonLabel
}) => {
	const enhancedOptions = useMemo(() => {
		return options.map(option => ({
			...option,
			compositeKey: buildCompositeKey(option)
		}));
	}, [ options ]);

	const initialSelectionMap = useMemo(() => {
		return isNonEmptyArray(initialSelections.filter(Boolean))
			? new Map(initialSelections.map(selection => ([ buildCompositeKey(selection), selection ])))
			: new Map();
	}, [ initialSelections ]);

	const [ selections, setSelections ] = useState(initialSelectionMap);
	const [ hasSelections, setHasSelections ] = useState(isNonEmptyMap(selections));
	const [ isSelectAll, setIsSelectAll ] = useState(false);
	const [ search, setSearch ] = useState("");
	const [ updateMessage, setUpdateMessage ] = useState("");
	const dictionary = useDictionary("common.downloadCsv");
	const ariaControlsAttr = isOpen ? { [`aria-controls`]: MODAL_ID } : {};

	React.useEffect(() => {
		if (isOpen) {
			setSelections(initialSelectionMap);
			setSearch("");
			setIsSelectAll(false);
			setHasSelections(isNonEmptyMap(initialSelectionMap));
		}
	}, [ isOpen, initialSelectionMap ]);

	const sortWithoutSearch = useCallback(() => {
		const selectedOptions = [];
		const unselectedOptions = [];
		const mutableOptions = [ ...enhancedOptions ].filter(({ hiddenFromUI }) => hiddenFromUI !== true);

		mutableOptions.forEach(option => {
			const isSelected = selections.has(option.compositeKey);

			isSelected
				? selectedOptions.push(option)
				: unselectedOptions.push(option);
		});

		alphaSortCollectionByKey(selectedOptions, "columnHeader");
		alphaSortCollectionByKey(unselectedOptions, "columnHeader");

		return [
			...selectedOptions,
			...unselectedOptions
		];
	}, [ enhancedOptions, selections ]);

	const sortWithSearch = useCallback(() => {
		const lcSearch = search.toLowerCase();
		const filteredOptions = [ ...enhancedOptions ].filter(option => {
			return option.columnHeader.toLowerCase().includes(lcSearch) && option.hiddenFromUI !== true;
		});

		alphaSortCollectionByKey(filteredOptions, "columnHeader");

		return filteredOptions;
	}, [ enhancedOptions, search ]);

	const sortedOptions = useMemo(() => {
		return isNonEmptyString(search)
			? sortWithSearch()
			: sortWithoutSearch();
	}, [ search, sortWithSearch, sortWithoutSearch ]);

	const hasOptions = isNonEmptyArray(sortedOptions);

	const handleButtonClick = () => {
		if (isFunction(onClick)) {
			onClick();
		}
	};

	const handleCancel = () => {
		if (isFunction(onCancel)) {
			onCancel();
		}

		const triggerButton = document.getElementById("downloadToCSVModalBtn");

		if (triggerButton) {
			triggerButton.focus();
		}
	};

	const handleSave = useCallback(() => {
		if (isFunction(onSave)) {
			onSave({ selections: Array.from(selections.values()) });
		}
	 }, [ onSave, selections ]);

	const onSearchChange = ({ value }) => {
		setSearch(value);
	};

	const toggleSelection = (option, nextOption) => {
		const updatedSelections = new Map(selections);
		if (isNonEmptyString(option?.id)) {
			const key = option.compositeKey;
			updatedSelections.has(key)
				? updatedSelections.delete(key)
				: updatedSelections.set(key, option);

			setSelections(updatedSelections);
			setHasSelections(isNonEmptyMap(updatedSelections));

			handleColumnsListFocus(nextOption);

			setUpdateMessage(`List updated. Option ${option.columnHeader} ${updatedSelections.has(key) ? "selected" : "unselected"}`);
		}
	};

	const toggleIsSelectAll = () => {
		const willBeSelectAll = !isSelectAll;
		const updatedSelections = new Map();

		if (willBeSelectAll) {
			sortedOptions.forEach(option => {
				updatedSelections.set(option.compositeKey, option);
			});
		}

		setHasSelections(isNonEmptyMap(updatedSelections));
		setSelections(updatedSelections);
		setIsSelectAll(willBeSelectAll);
	};

	return (
		<div data-component="DownloadCSV" className={styles.downloadCSV}>
			{showButton &&
				<IconButton
					{...ariaControlsAttr}
					icon="download"
					labelPosition="left"
					onClick={handleButtonClick}
					aria-haspopup="dialog"
					id="downloadToCSVModalBtn"
				>
					{dictionary.triggerButtonLabel || "Download to CSV"}
				</IconButton>
			}

			<Modal
				id={MODAL_ID}
				open={isOpen}
				onClose={handleCancel}
				size="one-half"
				closeIcon={!isDownloading}
				ariaLabel={popupTitle || dictionary.title || "Download to CSV"}
			>
				<Modal.Header className={`ui header ${styles.modalHeading} whiteOnBlue`}>
					<h2>{popupTitle || dictionary.title || "Download to CSV"}</h2>
				</Modal.Header>

				<Modal.Content className={styles.body}>
					{isDownloading
						? <DownloadingModalContent text={dictionary.downloadingProgress} />
						: (
							<FormModalContent
								dictionary={dictionary}
								onSearchChange={onSearchChange}
								search={search}
								toggleIsSelectAll={toggleIsSelectAll}
								isSelectAll={isSelectAll}
								hasOptions={hasOptions}
								sortedOptions={sortedOptions}
								selections={selections}
								toggleSelection={toggleSelection}
								popupDescription={popupDescription}
								updateMessage={updateMessage}
							/>
						)}
				</Modal.Content>

				{!isDownloading && (
					<Modal.Actions>
						<div className="field">
							<ButtonGroup>
								<Button secondary onClick={handleCancel}>
									{dictionary.cancelButtonLabel || "Cancel"}
								</Button>
								<Button
									primary
									onClick={handleSave}
									disabled={isDownloading || !hasSelections}
									busy={isDownloading}
									loadingMessage="Download in process"
								>
									{executeButtonLabel || dictionary.downloadButtonLabel || "Download to CSV"}
								</Button>
							</ButtonGroup>
						</div>
					</Modal.Actions>
				)}
			</Modal>
		</div>
	);
};

DownloadCSVModal.propTypes = {
	onCancel: PropTypes.func,
	onClick: PropTypes.func,
	onSave: PropTypes.func,
	open: PropTypes.bool,
	options: PropTypes.arrayOf(PropTypes.shape({
		columnHeader: PropTypes.string,
		keys: PropTypes.arrayOf(
			PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.number
			])
		),
		id: PropTypes.string
	}))
};

export default DownloadCSVModal;
